import type { FigmaFilesAPIData } from "@/src/types/index.ts";
import type { ZeytalCLIPureConfig } from "@zeytal/kit";
import type { ZeytalApi } from "@zeytal/types";
import type { OptionValues } from "commander";

import * as path from "node:path";

import { zeytalDefaultConfig } from "@zeytal/kit";
import { projectTokenHeaderName } from "@zeytal/kit/api";
import {
  getMappedStyles,
  getNestedNodes,
  makeStylesStructure,
  transformStylesValues,
  translateStyles,
} from "@zeytal/tokens";
import { cyan, magenta } from "colorette";
import { Command } from "commander";

import { fetchFilesData, fetchFilesVersions } from "@/src/lib/figma.ts";
import { errorMessages } from "@/src/utils/errors.ts";
import { refreshFolder, writeFile } from "@/src/utils/file-system.ts";
import { getConfig } from "@/src/utils/get-config.ts";
import { getCWD } from "@/src/utils/get-cwd.ts";
import { getZeytalApiBaseUrl } from "@/src/utils/get-zeytal-api-base-url.ts";
import { icons } from "@/src/utils/icons.ts";
import { logger } from "@/src/utils/logger.ts";

const stylesActionCore = async (
  commandOptions: OptionValues,
  zeytalConfig: ZeytalCLIPureConfig,
) => {
  logger.log(`${magenta(icons.leftHalfCircle)} Styles fetching started...`);

  logger.spinner().start("Assessing your configuration parameters...");

  const zeytalAPIBaseUrl = getZeytalApiBaseUrl();

  logger.debug("Getting user’s zeytal project data and figma files list.");

  const figmaStylesAPIRes = await fetch(`${zeytalAPIBaseUrl}/api/styles`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      [projectTokenHeaderName]: zeytalConfig.projectToken ?? "",
    },
  }).catch(() => {
    logger.spinner().fail();
    throw errorMessages.zeytalApiError500();
  });

  const figmaStylesAPIData =
    (await figmaStylesAPIRes.json()) as ZeytalApi.ApiResponse<FigmaFilesAPIData>;

  if (!figmaStylesAPIData || !figmaStylesAPIData.success) {
    logger.spinner().fail();
    throw figmaStylesAPIData.message;
  }

  if (!figmaStylesAPIData.data || figmaStylesAPIData.data === null) {
    logger.spinner().fail();
    throw errorMessages.zeytalApiNoData();
  }

  logger.spinner().succeed();

  logger
    .spinner()
    .start(
      `Searching for the latest ${zeytalConfig.styles.figmaFilesVersion} version of your figma files...`,
    );

  if (!figmaStylesAPIData.data.projectData.figmaApiKey) {
    logger.spinner().fail();
    throw errorMessages.noFigmaApiKey({
      projectName: figmaStylesAPIData.data.projectData.name,
    });
  }

  // Get the right files versions
  const filesWithVersion =
    (await fetchFilesVersions(
      figmaStylesAPIData.data.figmaFiles,
      figmaStylesAPIData.data.projectData.figmaApiKey,
      zeytalConfig,
      "styles",
    )) ?? [];

  logger.spinner().succeed();

  logger.spinner().start("Figma files fetching started...");

  const currentPagesBatchSize = zeytalConfig.styles.figmaFilesPagesBatchSize;
  const cleanPagesBatchSize =
    !currentPagesBatchSize ||
    typeof currentPagesBatchSize !== "number" ||
    currentPagesBatchSize <= 1
      ? zeytalDefaultConfig.styles.figmaFilesPagesBatchSize
      : currentPagesBatchSize;

  logger.spinner().succeed();

  // Get the pages in the Figma files that are not empty
  const figmaFilesData = await fetchFilesData(
    cleanPagesBatchSize,
    filesWithVersion,
    figmaStylesAPIData.data.projectData.figmaApiKey,
  );

  logger.spinner().start("Processing your figma files data...");

  logger.debug("Nesting Figma files nodes.");
  const nestedNodes = getNestedNodes(figmaFilesData);

  logger.spinner().succeed();

  logger.spinner().start("Extracting your styles...");

  logger.debug("Mapping Figma styles.");
  const mappedStyles = getMappedStyles(figmaFilesData, nestedNodes);

  logger.debug("Structuring Figma styles.");
  const structuredStyles = makeStylesStructure(mappedStyles);

  logger.spinner().succeed();

  logger.spinner().start("Remodelling your styles values...");

  logger.debug("Transforming Figma styles values.");
  const transformedStyles = transformStylesValues(
    structuredStyles,
    zeytalConfig,
  );

  logger.spinner().succeed();

  if (zeytalConfig.styles.formats.targets.length <= 0) {
    logger.warn(
      `${icons.warning} Your style's format target array is empty. No style translation will be performed.`,
    );
    return;
  }

  logger
    .spinner()
    .start(
      `Translating your styles into ${[
        ...new Set(zeytalConfig.styles.formats.targets),
      ]
        .map((t) => cyan(t))
        .join(", ")}.`,
    );

  logger.debug("Translating Figma styles.");
  const stylesFiles = translateStyles(transformedStyles, zeytalConfig);

  logger.spinner().succeed();

  logger.spinner().succeed("Styles extraction and remodelling done.");

  logger
    .spinner()
    .start(
      `Writing styles files inside ${cyan(zeytalConfig.styles.directoryPath)}...`,
    );

  logger.debug("Refreshing styles directory.");
  await refreshFolder(
    path.join(commandOptions.cwd, zeytalConfig.styles.directoryPath),
  );

  // Writing styles files
  Object.entries(stylesFiles!).forEach(([fileName, fileContent]) => {
    logger.debug("Writing styles file.");
    const writePath = path.join(
      commandOptions.cwd,
      zeytalConfig.styles.directoryPath,
      fileName,
    );
    writeFile(writePath, fileContent);
  });

  logger
    .spinner()
    .succeed(
      `All styles files written to ${cyan(zeytalConfig.styles.directoryPath)}`,
    );
};

export const stylesCmd = new Command()
  .name("styles")
  .description("extract and transform Figma styles from your Figma files")
  .action(async (_, command) => {
    logger.break();

    const optsWithGlobals = command.optsWithGlobals();
    logger.setDebugModeTo(optsWithGlobals.debug);

    const options = { ...optsWithGlobals, cwd: getCWD(optsWithGlobals.cwd) };

    logger.debug("Getting user’s zeytal CLI config.");
    const zeytalConfig = await getConfig(options.cwd);

    try {
      await stylesActionCore(options, zeytalConfig);
    } catch (errorMessage) {
      logger.error(errorMessage);
      logger.break();
      process.exit(1);
    }
  });
